import { ruleSizeForPayload, type ConfigSecgroup, type SecgroupRuleTpl } from '@/api/opennebula';

interface SecgroupRulePayload {
  ref_type?: string;
  rule_type: 'INBOUND' | 'OUTBOUND';
  protocol: string;
  range: string;
  ip: string;
  /** Address count for a bare-ip range; omitted when `ip` carries a CIDR suffix. */
  size?: string;
}

interface SecgroupPayload {
  name: string;
  inbound: SecgroupRulePayload[];
  outbound: SecgroupRulePayload[];
}

/**
 * Default firewall used when the user attaches no security group — mirrors the
 * legacy @console create flow: SSH inbound; ICMP + all-TCP/UDP outbound.
 */
function defaultSecgroup(): SecgroupPayload {
  return {
    name: `secgroup_${Date.now()}`,
    inbound: [{ ref_type: 'SSH', rule_type: 'INBOUND', protocol: 'TCP', range: '22', ip: '' }],
    outbound: [
      { ref_type: 'ICMP', rule_type: 'OUTBOUND', protocol: 'ICMP', range: '', ip: '' },
      { ref_type: 'ALL TCP', rule_type: 'OUTBOUND', protocol: 'TCP', range: '1:24,26:65535', ip: '' },
      { ref_type: 'ALL UDP', rule_type: 'OUTBOUND', protocol: 'UDP', range: '1:24,26:65535', ip: '' },
    ],
  };
}

/**
 * Convert an existing security group into the rule-object shape the create handler
 * requires (name + inbound/outbound arrays), splitting its rules by direction. The
 * Ruby backend iterates `secgroups` doing `sg[:key]`, so each element MUST be an
 * object — a bare id string throws "no implicit conversion of Symbol into Integer".
 */
function toSecgroupPayload(sg: ConfigSecgroup): SecgroupPayload {
  const rules = sg.template?.rule ?? [];
  const norm = (r: SecgroupRuleTpl): SecgroupRulePayload => {
    const ip = r.ip ?? '';
    return {
      ref_type: r.ref_type,
      rule_type: r.rule_type === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND',
      protocol: r.protocol ?? 'TCP',
      range: r.range ?? '',
      ip,
      ...ruleSizeForPayload(ip, r.size),
    };
  };
  return {
    name: sg.name ?? sg.id,
    inbound: rules.filter((r) => r.rule_type === 'INBOUND').map(norm),
    outbound: rules.filter((r) => r.rule_type === 'OUTBOUND').map(norm),
  };
}

/**
 * Build the `{ secgroup, secgroups }` portion of the instance-create payload exactly
 * as legacy @console does: a required `secgroup` object (the first attached group, or
 * the default firewall when none) plus, when groups are attached, `secgroups` as an
 * array of those rule objects.
 */
export function buildSecgroupPayload(selectedIds: string[], all: ConfigSecgroup[]): {
  secgroup: SecgroupPayload;
  secgroups?: SecgroupPayload[];
} {
  const picked = selectedIds
    .map((id) => all.find((s) => String(s.id) === String(id)))
    .filter((s): s is ConfigSecgroup => Boolean(s))
    .map(toSecgroupPayload);
  if (picked.length === 0) return { secgroup: defaultSecgroup() };
  return { secgroup: picked[0], secgroups: picked };
}
